import type {PayloadAction} from '@reduxjs/toolkit';
import type {PageApplyMode} from '@Components/poster-editor/components/add-new-page-button/add-new-page-button.types';
import {createSelector, createSlice} from '@reduxjs/toolkit';
import type {PosterObject, PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import type {PosterScalingObject} from '@PosterWhiteboard/poster/poster-scaling.class';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';
import type {CopyableItemStyles, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {PageTransitionsObject, TransitionBoundaryId} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {
  AiImageEditState,
  AiImageGenerationFailureState,
  AiImageGenerationState,
  AiState,
  AiVideosState,
  PosterAnimationGenerationState,
} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {
  AiImageGenerationFailureStatus,
  AiImageState,
  AiSubtitleState,
  AiVideoGenerationStatus,
  AiVoiceState,
  PosterAnimationGenerationStatus,
} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import type {SuggestionItems} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.types';
import type {RootState} from '@/store';
import {getPagePlaybackTime} from '@Components/poster-editor/poster-session-reducer';
import type {DeepPartial} from '@/global';
import type {FancyTextItemObject} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {FeatureDemo} from '@Components/poster-editor/poster-editor.types';
import {MobilePageSelectionModeState} from '@PosterWhiteboard/poster/poster.types';
import type {UserSaveConflictPopupProps} from '@Components/user-save-conflict-popup';
import type {PosterVersionData} from '@Components/poster-editor/library/poster-editor-library';
import {getPersistedIsOnMyDesignExpanded} from '@Components/poster-editor/library/poster-editor-storage-library';
import {isPageVideo as isPageVideoUtil} from '@Libraries/page-object.library';
import {computePageDuration, getPageDuration} from '@Libraries/page-duration.library';
import type {ItemLoadingProgressType, ItemLoadingReason} from '@PosterWhiteboard/items/item/item.types';
import type {AiProcessingStatus} from '@PosterWhiteboard/items/item/item.types';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {omitKey} from '@Utils/record.util';
import type {PageItemsObject} from '@PosterWhiteboard/page/page-items.class';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import type {LayerEntry} from '@Components/poster-editor/components/layers-panel/layers-panel.types';
import {LayerType} from '@Components/poster-editor/components/layers-panel/layers-panel.types';
import {areLayerEntriesEqual} from '@Components/poster-editor/components/layers-panel/layers-panel.helper';
import {shallowEqual} from 'react-redux';
import type {SelectedTimelineItem} from '@Components/poster-editor/components/timeline/timeline.types';
import {TimelineItemType} from '@Components/poster-editor/components/timeline/timeline.types';
import {isUserUploadedImageSource} from '@Libraries/image-item.library';
import {AiAnimationSourceType} from '@Libraries/ai-poster-animation.library';

export interface SignageRenderStateProps {
  isSignageRenderOutdated: boolean;
  signageOrderId: number | null;
}

export interface UpdatePosterDataProps {
  posterObject?: PosterObject;
  hasUndo?: boolean;
  hasUnsavedChanges?: boolean;
  hasSavedSinceLoad?: boolean;
  hasRedo?: boolean;
  isVideo?: boolean;
  currentPageId?: string;
  activeItemIds?: string[];
  isPosterVideo?: boolean;
}

interface UpdateIsDesktopTimelineToggleOnProps {
  isDesktopTimelineToggleOn: boolean;
  pageIdToScrollToOnWhiteboardResize?: string;
}

interface UpdatePageDataPayload {
  pageHashId: string;
  obj: DeepPartial<PageObject>;
}

interface UpdateAudioItemIsPlayingPayload {
  audioItemUID: string;
  isPlaying: boolean;
}

interface UpdateArePosterItemsModifyingProps {
  pageHashedId: string;
  isModifying: boolean;
}

export interface UpdateShouldHideControlsProps {
  activeItemHashId: string;
  shouldHideControls: boolean;
}

interface UpdateActiveItemsOnPagePayload {
  pageHashedId: string;
  activeItemIds: string[];
}

interface UpdateHoveredItemProps {
  pageHashedId: string;
  hoveredItemId: string | undefined;
}

interface UpdateMarqueePreviewItemsPayload {
  pageHashedId: string;
  itemIds: string[];
}

export interface ItemLoadingState {
  isVisible: boolean;
  progress: number;
  langTextKeys?: string[];
  progressType?: ItemLoadingProgressType;
  reason?: ItemLoadingReason;
  hideText?: boolean;
}

interface UpdateItemLoadingStatePayload {
  pageHashedId: string;
  itemId: string;
  state: ItemLoadingState | null;
}

interface StateProps {
  posterObject: PosterObject | null;
  hasUndo: boolean;
  isPosterSaving: boolean;
  didPosterSaveFailed: boolean;
  arePosterItemsMoving: Record<string, boolean>;
  arePosterItemsScaling: Record<string, boolean>;
  arePosterItemsRotating: Record<string, boolean>;
  shouldHideControls: Record<string, boolean>;
  didPosterRecentlySave: boolean;
  hasUnsavedChanges: boolean;
  hasSavedSinceLoad: boolean;
  isSignageRenderOutdated: boolean;
  signageOrderId: number | null;
  hasRedo: boolean;
  isVideo: boolean;
  currentPageId: string;
  isEmbeddedExportMode: boolean;
  activeItemIds: Record<string, string[]>;
  /**
   * The item ids that contextual option panels should target, set when a group editing panel is
   * opened from the mobile group bar (web delivers this via React context instead — see
   * useContextualTargetItemIds). Null when no group scope is active. Cleared whenever the active
   * selection changes, so single-item panels never see stale group ids.
   */
  contextualScopeItemIds: string[] | null;
  /**
   * Monotonically incremented on every undo/redo. Group option sliders hold a relative-anchor
   * "session" value (re-seeded when the selection changes); they also watch this nonce so a history
   * navigation re-anchors them, since their underlying item values changed from outside the slider.
   */
  historyNavigationNonce: number;
  /**
   * Advances on every poster publish (`updatePosterData` carrying a poster object) and never
   * resets, so caches keyed on it stay valid across poster boots. Playback ticks and item-drag
   * ticks do not advance it.
   */
  commitId: number;
  /**
   * The current page's audio selection (one uid = single, two+ = multi). Slice-level
   * like activeItemIds — selection is UI state and the serialized posterObject
   * carries no audio selection at all, so this is the ONLY Redux source for it.
   */
  selectedAudioItemUIDs: string[];
  hoveredItemId: Record<string, string>;
  // Items on a page that the in-progress marquee box currently covers — the set that a mouseup
  // would select. Rendered as hover boxes so the user can see what the marquee will grab.
  marqueePreviewItemIds: Record<string, string[]>;
  isPosterVideo?: boolean;
  isSpellCheckSettingsActive?: boolean;
  isSidebarOpen: boolean;
  isSidebarSmall?: boolean;
  isMobileVariant?: boolean;
  /** The mobile timeline panel has finished opening and now owns its layout slot; false while it is closed or still animating. */
  isMobileTimelinePanelSettledOpen?: boolean;
  isPickingModeEnabled?: boolean;
  isRemoveBGModeEnabled?: boolean;
  aiState: AiState;
  copiedStyles?: CopyableItemStyles;
  isUploadingToMyUploads: boolean;
  isPublishDropDownOpen: boolean;
  isShareDropDownOpen: boolean;
  inputSuggestions?: SuggestionItems;
  businessAddressSuggestions?: SuggestionItems;
  showAutoSaveTooltip: boolean;
  haveShownAutoSaveTooltip: boolean;
  isPosterOverlayLoaderActive: boolean;
  isEditableTextLayersGenerating: boolean;
  isVersionHistoryOverlayActive: boolean;
  versionHistorySelectedVersion: PosterVersionData | null;
  currentUserId?: number;
  /** Set by surfaces outside the add-item bar (add-page caret, timeline "+") to open the bar's popup in a given apply mode; null when idle. */
  triggerOpenTemplatesPopup: PageApplyMode | null;
  triggerOpenMyDesignsPopup: PageApplyMode | null;
  featureToShowOnLoad?: FeatureToShowOnLoad;
  featureDemo?: FeatureDemo;
  triggerOpenRecordOptions?: boolean;
  isPosterShared?: boolean;
  isPosterSharedInTeam?: boolean;
  saveConflictData?: SaveConflictData;
  isVersionHistoryModeActive: boolean;
  isDesktopTimelineToggleOn?: boolean;
  fitToScreenOnWhiteboardResize: boolean;
  pageIdToScrollToOnWhiteboardResize?: string;
  itemLoadingStates: Record<string, Record<string, ItemLoadingState>>;
  activeAiProcessing: Record<string, AiProcessingStatus>;
  mobilePageSelectionModeState: MobilePageSelectionModeState;
  pagePreviews: Record<string, string>;
  triggerOpenDownloadWebPanel?: boolean;
  triggerOpenPosterAnimationPanel?: boolean;
  isPosterInitialized: boolean;
  isOnMyDesignExpanded: boolean;
  openAnimationPanelRequested?: boolean;
  closeAnimationPanelRequested?: boolean;
  /** Boundary (see TransitionBoundaryId) whose transition editor the sidebar should open, or null. */
  openBackgroundTransitionPanelRequest: string | null;
  /**
   * Boundary whose transition editor is CURRENTLY open on any surface (sidebar drill-down,
   * region panel, mobile sheet) — published by the shared editor on mount/unmount. The
   * timeline's add-chip renders its selected state from this.
   */
  activeTransitionEditorBoundaryId: string | null;
  isAnimationPanelActiveInSidebar?: boolean;
  isLayersPanelOpen: boolean;
  hasShownOnboardingPopup: boolean;
  hasShownItemAnimationOnboardingPopup: boolean;
}

interface SaveConflictData extends UserSaveConflictPopupProps {
  triggerOpenSaveConflictPopup: boolean;
}

const initialState: StateProps = {
  hasUndo: false,
  isPosterSaving: false,
  didPosterSaveFailed: false,
  arePosterItemsMoving: {},
  arePosterItemsScaling: {},
  arePosterItemsRotating: {},
  shouldHideControls: {},
  didPosterRecentlySave: false,
  hasRedo: false,
  hasUnsavedChanges: false,
  hasSavedSinceLoad: false,
  isSignageRenderOutdated: false,
  signageOrderId: null,
  isVideo: false,
  isEmbeddedExportMode: false,
  currentPageId: '',
  hasShownOnboardingPopup: false,
  hasShownItemAnimationOnboardingPopup: false,
  posterObject: null,
  activeItemIds: {},
  selectedAudioItemUIDs: [],
  contextualScopeItemIds: null,
  historyNavigationNonce: 0,
  commitId: 0,
  hoveredItemId: {},
  marqueePreviewItemIds: {},
  isMobileVariant: isEditorMobileVariant(),
  isMobileTimelinePanelSettledOpen: false,
  isPosterVideo: false,
  isSpellCheckSettingsActive: false,
  isSidebarOpen: true,
  isSidebarSmall: false,
  isPickingModeEnabled: false,
  isRemoveBGModeEnabled: false,
  aiState: {
    aiImage: AiImageState.IDLE,
    aiSubtitles: AiSubtitleState.IDLE,
    aiVoice: AiVoiceState.IDLE,
    aiVideo: [],
    aiPosterAnimation: [],
    aiImageEdit: [],
    aiImageGenerations: [],
    aiImageGenerationFailures: [],
    aiCredits: null,
    hasUserSeenImageEdit: true,
    hasUserSeenImageUpscale: true,
  },
  isUploadingToMyUploads: false,
  isPublishDropDownOpen: false,
  isShareDropDownOpen: false,
  showAutoSaveTooltip: false,
  haveShownAutoSaveTooltip: false,
  copiedStyles: undefined,
  isPosterOverlayLoaderActive: false,
  isEditableTextLayersGenerating: false,
  isVersionHistoryOverlayActive: false,
  versionHistorySelectedVersion: null,
  triggerOpenTemplatesPopup: null,
  triggerOpenMyDesignsPopup: null,
  triggerOpenRecordOptions: false,
  featureDemo: FeatureDemo.NONE,
  isPosterShared: false,
  isPosterSharedInTeam: false,
  saveConflictData: undefined,
  isDesktopTimelineToggleOn: false,
  fitToScreenOnWhiteboardResize: false,
  pageIdToScrollToOnWhiteboardResize: undefined,
  isVersionHistoryModeActive: false,
  itemLoadingStates: {},
  activeAiProcessing: {},
  mobilePageSelectionModeState: MobilePageSelectionModeState.INACTIVE,
  pagePreviews: {},
  triggerOpenDownloadWebPanel: false,
  triggerOpenPosterAnimationPanel: false,
  isPosterInitialized: false,
  isOnMyDesignExpanded: getPersistedIsOnMyDesignExpanded(),
  openAnimationPanelRequested: false,
  closeAnimationPanelRequested: false,
  openBackgroundTransitionPanelRequest: null,
  activeTransitionEditorBoundaryId: null,
  isAnimationPanelActiveInSidebar: false,
  isLayersPanelOpen: false,
};

const posterEditorSlice = createSlice({
  name: 'posterEditor',
  initialState,
  reducers: {
    updatePosterData: (state, action: PayloadAction<UpdatePosterDataProps>) => {
      if (action.payload.posterObject !== undefined) {
        state.posterObject = action.payload.posterObject;
        state.commitId += 1;
      }
      if (action.payload.hasUndo !== undefined) {
        state.hasUndo = action.payload.hasUndo;
      }
      if (action.payload.hasUnsavedChanges !== undefined) {
        state.hasUnsavedChanges = action.payload.hasUnsavedChanges;
      }
      if (action.payload.hasSavedSinceLoad !== undefined) {
        state.hasSavedSinceLoad = action.payload.hasSavedSinceLoad;
      }
      if (action.payload.currentPageId !== undefined) {
        state.currentPageId = action.payload.currentPageId;
      }
      if (action.payload.hasRedo !== undefined) {
        state.hasRedo = action.payload.hasRedo;
      }
      if (action.payload.isVideo !== undefined) {
        state.isVideo = action.payload.isVideo;
      }
      if (action.payload.isPosterVideo !== undefined) {
        state.isPosterVideo = action.payload.isPosterVideo;
      }
    },
    setPosterOverlayLoaderVisibility: (state, action: PayloadAction<boolean>) => {
      state.isPosterOverlayLoaderActive = action.payload;
    },
    setIsEditableTextLayersGenerating: (state, action: PayloadAction<boolean>) => {
      state.isEditableTextLayersGenerating = action.payload;
    },
    setHasShownOnboardingPopup: (state, action: PayloadAction<boolean>) => {
      state.hasShownOnboardingPopup = action.payload;
    },
    setHasShownItemAnimationOnboardingPopup: (state, action: PayloadAction<boolean>) => {
      state.hasShownItemAnimationOnboardingPopup = action.payload;
    },
    setVersionHistoryOverlayVisibility: (state, action: PayloadAction<boolean>) => {
      state.isVersionHistoryOverlayActive = action.payload;
    },
    setVersionHistorySelectedVersion: (state, action: PayloadAction<PosterVersionData | null>) => {
      state.versionHistorySelectedVersion = action.payload;
    },
    restoreInitialData: (state) => {
      state.didPosterSaveFailed = initialState.didPosterSaveFailed;
      state.arePosterItemsScaling = initialState.arePosterItemsScaling;
      state.arePosterItemsMoving = initialState.arePosterItemsMoving;
      state.arePosterItemsRotating = initialState.arePosterItemsRotating;
      state.didPosterRecentlySave = initialState.didPosterRecentlySave;
      state.hasSavedSinceLoad = initialState.hasSavedSinceLoad;
      state.isSignageRenderOutdated = initialState.isSignageRenderOutdated;
      state.signageOrderId = initialState.signageOrderId;
      state.activeItemIds = initialState.activeItemIds;
      // A new Poster boots with an empty AudioClips selection; the mirror must reset with it
      // or a stale selection survives dispose and contradicts the class state.
      state.selectedAudioItemUIDs = initialState.selectedAudioItemUIDs;
      state.contextualScopeItemIds = initialState.contextualScopeItemIds;
      state.hoveredItemId = initialState.hoveredItemId;
      state.marqueePreviewItemIds = initialState.marqueePreviewItemIds;
      state.shouldHideControls = initialState.shouldHideControls;
      state.fitToScreenOnWhiteboardResize = initialState.fitToScreenOnWhiteboardResize;
      state.pageIdToScrollToOnWhiteboardResize = initialState.pageIdToScrollToOnWhiteboardResize;
      state.isPosterInitialized = initialState.isPosterInitialized;
      state.isLayersPanelOpen = initialState.isLayersPanelOpen;
    },
    updateHasUndo: (state, action: PayloadAction<boolean>) => {
      state.hasUndo = action.payload;
    },
    updateIsPosterSaving: (state, action: PayloadAction<boolean>) => {
      state.isPosterSaving = action.payload;
    },
    updateDidPosterSaveFailed: (state, action: PayloadAction<boolean>) => {
      state.didPosterSaveFailed = action.payload;
    },
    updateArePosterItemsMoving: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsMoving[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateArePosterItemsScaling: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsScaling[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateArePosterItemsRotating: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsRotating[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateShouldHideControls: (state, action: PayloadAction<UpdateShouldHideControlsProps>) => {
      state.shouldHideControls[action.payload.activeItemHashId] = action.payload.shouldHideControls;
    },
    onPosterItemsModified: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.arePosterItemsMoving[action.payload];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.arePosterItemsScaling[action.payload];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.arePosterItemsRotating[action.payload];
    },
    updatePageData: (state, action: PayloadAction<UpdatePageDataPayload>) => {
      if (!state.posterObject) return;

      const {pageHashId, obj} = action.payload;
      const page = state.posterObject.pages.pagesHashMap[pageHashId];

      if (!page) return;

      if (obj.isPlaying !== undefined) {
        page.isPlaying = obj.isPlaying;
      }
    },
    updateDidPosterRecentlySave: (state, action: PayloadAction<boolean>) => {
      state.didPosterRecentlySave = action.payload;
    },
    updateSignageRenderState: (state, action: PayloadAction<SignageRenderStateProps>) => {
      state.isSignageRenderOutdated = action.payload.isSignageRenderOutdated;
      state.signageOrderId = action.payload.signageOrderId;
    },
    updateIsEmbeddedExportMode: (state, action: PayloadAction<boolean>) => {
      state.isEmbeddedExportMode = action.payload;
    },
    updateHasRedo: (state, action: PayloadAction<boolean>) => {
      state.hasRedo = action.payload;
    },
    updateIsPosterInitialized: (state, action: PayloadAction<boolean>) => {
      state.isPosterInitialized = action.payload;
    },
    updateIsPosterEditorMobileVariant: (state, action: PayloadAction<boolean>) => {
      state.isMobileVariant = action.payload;
    },
    updateIsMobileTimelinePanelSettledOpen: (state, action: PayloadAction<boolean>) => {
      state.isMobileTimelinePanelSettledOpen = action.payload;
    },
    updateActiveItemsOnPage: (state, action: PayloadAction<UpdateActiveItemsOnPagePayload>) => {
      state.activeItemIds[action.payload.pageHashedId] = action.payload.activeItemIds;
      // The contextual scope is bound to a specific selection; once the selection changes it is
      // stale, so drop it. A new group panel sets it again on open.
      state.contextualScopeItemIds = null;
    },
    setContextualScope: (state, action: PayloadAction<string[]>) => {
      state.contextualScopeItemIds = action.payload;
    },
    markHistoryNavigation: (state) => {
      state.historyNavigationNonce += 1;
    },
    updateHoveredItem: (state, action: PayloadAction<UpdateHoveredItemProps>) => {
      if (action.payload.hoveredItemId) {
        state.hoveredItemId[action.payload.pageHashedId] = action.payload.hoveredItemId;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.hoveredItemId[action.payload.pageHashedId];
      }
    },
    updateMarqueePreviewItems: (state, action: PayloadAction<UpdateMarqueePreviewItemsPayload>) => {
      if (action.payload.itemIds.length > 0) {
        state.marqueePreviewItemIds[action.payload.pageHashedId] = action.payload.itemIds;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.marqueePreviewItemIds[action.payload.pageHashedId];
      }
    },
    updateItemLoadingState: (state, action: PayloadAction<UpdateItemLoadingStatePayload>) => {
      const {pageHashedId, itemId, state: loadingState} = action.payload;

      if (loadingState) {
        if (!state.itemLoadingStates[pageHashedId]) {
          state.itemLoadingStates[pageHashedId] = {};
        }
        state.itemLoadingStates[pageHashedId][itemId] = loadingState;
      } else {
        if (state.itemLoadingStates[pageHashedId]) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete state.itemLoadingStates[pageHashedId][itemId];
          if (Object.keys(state.itemLoadingStates[pageHashedId]).length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete state.itemLoadingStates[pageHashedId];
          }
        }
      }
    },
    setAudioSelection: (state, action: PayloadAction<string[]>) => {
      state.selectedAudioItemUIDs = action.payload;
    },
    setItemAiProcessing: (state, action: PayloadAction<{itemId: string; status: AiProcessingStatus}>) => {
      state.activeAiProcessing[action.payload.itemId] = action.payload.status;
    },
    clearItemAiProcessing: (state, action: PayloadAction<string>) => {
      state.activeAiProcessing = omitKey(state.activeAiProcessing, action.payload);
    },
    updateAudioItemIsPlaying: (state, action: PayloadAction<UpdateAudioItemIsPlayingPayload>) => {
      if (state.posterObject) {
        const currentPage = state.posterObject.pages.pagesHashMap[state.currentPageId];
        if (currentPage?.audioClips.audioItemsHashMap[action.payload.audioItemUID]) {
          currentPage.audioClips.audioItemsHashMap[action.payload.audioItemUID].audioPlayer.isPlaying = action.payload.isPlaying;
        }
      }
    },
    updateSpellCheckSettingsState: (state, action: PayloadAction<boolean>) => {
      state.isSpellCheckSettingsActive = action.payload;
    },
    updateSidebarState: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },
    updateCollapsableSidebarState: (state, action: PayloadAction<boolean>) => {
      state.isSidebarSmall = action.payload;
    },
    updatePickingMode: (state, action: PayloadAction<boolean>) => {
      state.isPickingModeEnabled = action.payload;
    },
    updateRemoveBgMode: (state, action: PayloadAction<boolean>) => {
      state.isRemoveBGModeEnabled = action.payload;
    },
    updateAiImageState: (state, action: PayloadAction<AiImageState>) => {
      state.aiState.aiImage = action.payload;
    },
    updateAiSubtitleState: (state, action: PayloadAction<AiSubtitleState>) => {
      state.aiState.aiSubtitles = action.payload;
    },
    updateAiVoiceState: (state, action: PayloadAction<AiVoiceState>) => {
      state.aiState.aiVoice = action.payload;
    },
    addAiVideoGenerationItem: (state, action: PayloadAction<AiVideosState>) => {
      state.aiState.aiVideo.unshift(action.payload);
    },
    markAiVideoGenerationFailed: (state, action: PayloadAction<{id: string; errorCode: string | undefined}>) => {
      const item = state.aiState.aiVideo.find((i) => i.id === action.payload.id);
      if (item) {
        item.status = AiVideoGenerationStatus.ERROR;
        item.errorCode = action.payload.errorCode;
      }
    },
    removeAiVideoGenerationItem: (state, action: PayloadAction<string>) => {
      state.aiState.aiVideo = state.aiState.aiVideo.filter((item) => item.id !== action.payload);
    },
    addAiImageEditGenerationItem: (state, action: PayloadAction<AiImageEditState>) => {
      state.aiState.aiImageEdit.unshift(action.payload);
    },
    removeAiImageEditGenerationItem: (state, action: PayloadAction<string>) => {
      state.aiState.aiImageEdit = state.aiState.aiImageEdit.filter((item) => item.itemUid !== action.payload);
    },
    addAiImageGenerationItem: (state, action: PayloadAction<AiImageGenerationState>) => {
      state.aiState.aiImageGenerations.push(action.payload);
    },
    removeAiImageGenerationItem: (state, action: PayloadAction<string>) => {
      state.aiState.aiImageGenerations = state.aiState.aiImageGenerations.filter((item) => item.id !== action.payload);
    },
    addAiImageGenerationFailure: (state, action: PayloadAction<AiImageGenerationFailureState>) => {
      state.aiState.aiImageGenerationFailures.unshift(action.payload);
    },
    removeAiImageGenerationFailure: (state, action: PayloadAction<string>) => {
      state.aiState.aiImageGenerationFailures = state.aiState.aiImageGenerationFailures.filter((item) => item.id !== action.payload);
    },
    setAiCredits: (state, action: PayloadAction<number>) => {
      state.aiState.aiCredits = action.payload;
    },
    addPosterAnimationGenerationItem: (state, action: PayloadAction<PosterAnimationGenerationState>) => {
      state.aiState.aiPosterAnimation.unshift(action.payload);
    },
    markPosterAnimationGenerationFailed: (state, action: PayloadAction<{id: string; errorCode: string | undefined}>) => {
      const item = state.aiState.aiPosterAnimation.find((i) => i.id === action.payload.id);
      if (item) {
        item.status = PosterAnimationGenerationStatus.ERROR;
        item.errorCode = action.payload.errorCode;
      }
    },
    removePosterAnimationGenerationItem: (state, action: PayloadAction<string>) => {
      state.aiState.aiPosterAnimation = state.aiState.aiPosterAnimation.filter((item) => item.id !== action.payload);
    },
    setHasUserSeenImageEdit: (state, action: PayloadAction<boolean>) => {
      state.aiState.hasUserSeenImageEdit = action.payload;
    },
    setHasUserSeenImageUpscale: (state, action: PayloadAction<boolean>) => {
      state.aiState.hasUserSeenImageUpscale = action.payload;
    },
    updateIsUploadingToMyUploads: (state, action: PayloadAction<boolean>) => {
      if (state.isUploadingToMyUploads !== action.payload) {
        state.isUploadingToMyUploads = action.payload;
      }
    },
    updatePublishDropDownState: (state, action: PayloadAction<boolean>) => {
      state.isPublishDropDownOpen = action.payload;
    },
    updateShareDropDownState: (state, action: PayloadAction<boolean>) => {
      state.isShareDropDownOpen = action.payload;
    },
    setInputSuggestions: (state, action: PayloadAction<SuggestionItems>) => {
      state.inputSuggestions = action.payload;
    },
    setBusinessAddress: (state, action: PayloadAction<SuggestionItems>) => {
      state.businessAddressSuggestions = action.payload;
    },
    updateAutoSaveTooltipState: (state, action: PayloadAction<boolean>) => {
      state.showAutoSaveTooltip = action.payload;
      if (action.payload) {
        state.haveShownAutoSaveTooltip = true;
      }
    },
    updateCopiedStylesState: (state, action: PayloadAction<CopyableItemStyles | undefined>) => {
      state.copiedStyles = action.payload;
    },
    updateCurrentUserId: (state, action: PayloadAction<number>) => {
      state.currentUserId = action.payload;
    },
    updateFeatureToShowOnLoad: (state, action: PayloadAction<FeatureToShowOnLoad>) => {
      state.featureToShowOnLoad = action.payload;
    },
    updateFeatureDemo: (state, action: PayloadAction<FeatureDemo>) => {
      state.featureDemo = action.payload;
    },
    updateRecordOptionsOpenState: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenRecordOptions = action.payload;
    },
    updateIsPosterShared: (state, action: PayloadAction<boolean>) => {
      state.isPosterShared = action.payload;
    },
    updateIsPosterSharedInTeam: (state, action: PayloadAction<boolean>) => {
      state.isPosterSharedInTeam = action.payload;
    },
    updateSaveConflictDataDetails: (state, action: PayloadAction<SaveConflictData | undefined>) => {
      state.saveConflictData = action.payload;
    },
    updateVersionHistoryModeState: (state, action: PayloadAction<boolean>) => {
      state.isVersionHistoryModeActive = action.payload;
    },
    updatePageIdToScrollToOnWhiteboardResize: (state, action: PayloadAction<string | undefined>) => {
      state.pageIdToScrollToOnWhiteboardResize = action.payload;
    },
    updateFitToScreenOnWhiteboardResize: (state, action: PayloadAction<boolean>) => {
      state.fitToScreenOnWhiteboardResize = action.payload;
    },
    updateIsDesktopTimelineToggleOn: (state, action: PayloadAction<UpdateIsDesktopTimelineToggleOnProps>) => {
      state.isDesktopTimelineToggleOn = action.payload.isDesktopTimelineToggleOn;
      state.fitToScreenOnWhiteboardResize = true;
      state.pageIdToScrollToOnWhiteboardResize = action.payload.pageIdToScrollToOnWhiteboardResize;
    },
    updatePosterScaling: (state, action: PayloadAction<PosterScalingObject>) => {
      if (state.posterObject) {
        state.posterObject.scaling = action.payload;
      }
    },
    setMobilePageSelectionModeState: (state, action: PayloadAction<MobilePageSelectionModeState>) => {
      state.mobilePageSelectionModeState = action.payload;
    },
    updatePagePreview: (state, action: PayloadAction<{pageHashedId: string; previewUrl: string}>) => {
      state.pagePreviews[action.payload.pageHashedId] = action.payload.previewUrl;
    },
    removePagePreview: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.pagePreviews[action.payload];
    },
    updateWebDownloadPanelOpenState: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenDownloadWebPanel = action.payload;
    },
    updateTriggerOpenPosterAnimationPanel: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenPosterAnimationPanel = action.payload;
    },
    updateIsOnMyDesignExpanded: (state, action: PayloadAction<boolean>) => {
      state.isOnMyDesignExpanded = action.payload;
    },
    updateTriggerOpenTemplatesPopup: (state, action: PayloadAction<PageApplyMode | null>) => {
      state.triggerOpenTemplatesPopup = action.payload;
    },
    updateTriggerOpenMyDesignsPopup: (state, action: PayloadAction<PageApplyMode | null>) => {
      state.triggerOpenMyDesignsPopup = action.payload;
    },
    requestOpenAnimationPanel: (state) => {
      state.openAnimationPanelRequested = true;
      state.isSidebarOpen = true;
    },
    clearOpenAnimationPanelRequest: (state) => {
      state.openAnimationPanelRequested = false;
    },
    requestCloseAnimationPanel: (state) => {
      state.closeAnimationPanelRequested = true;
    },
    clearCloseAnimationPanelRequest: (state) => {
      state.closeAnimationPanelRequested = false;
    },
    requestOpenBackgroundTransitionPanel: (state, action: PayloadAction<string>) => {
      state.openBackgroundTransitionPanelRequest = action.payload;
      state.isSidebarOpen = true;
    },
    clearOpenBackgroundTransitionPanelRequest: (state) => {
      state.openBackgroundTransitionPanelRequest = null;
    },
    setActiveTransitionEditorBoundaryId: (state, action: PayloadAction<string | null>) => {
      state.activeTransitionEditorBoundaryId = action.payload;
    },
    setSelectedTransitionBoundaryId: (state, action: PayloadAction<string>) => {
      if (state.posterObject) {
        const currentPage = state.posterObject.pages.pagesHashMap[state.currentPageId];
        if (currentPage) {
          currentPage.transitions.selectedBoundaryId = action.payload;
        }
      }
    },
    setIsAnimationPanelActiveInSidebar: (state, action: PayloadAction<boolean>) => {
      state.isAnimationPanelActiveInSidebar = action.payload;
    },
    updateLayersPanelState: (state, action: PayloadAction<boolean>) => {
      state.isLayersPanelOpen = action.payload;
    },
  },
});

export const {
  updatePosterData,
  setPosterOverlayLoaderVisibility,
  setIsEditableTextLayersGenerating,
  setHasShownOnboardingPopup,
  setHasShownItemAnimationOnboardingPopup,
  setVersionHistoryOverlayVisibility,
  setVersionHistorySelectedVersion,
  restoreInitialData,
  updateAudioItemIsPlaying,
  updateIsEmbeddedExportMode,
  setAudioSelection,
  updateActiveItemsOnPage,
  setContextualScope,
  markHistoryNavigation,
  updateHoveredItem,
  updateMarqueePreviewItems,
  updateItemLoadingState,
  setItemAiProcessing,
  clearItemAiProcessing,
  updateHasUndo,
  updateDidPosterRecentlySave,
  updateSignageRenderState,
  updateIsPosterSaving,
  updateDidPosterSaveFailed,
  updateArePosterItemsMoving,
  updateArePosterItemsRotating,
  updateArePosterItemsScaling,
  updateShouldHideControls,
  onPosterItemsModified,
  updatePageData,
  updateHasRedo,
  updateSpellCheckSettingsState,
  updateSidebarState,
  updateCollapsableSidebarState,
  updatePickingMode,
  updateRemoveBgMode,
  updateIsPosterEditorMobileVariant,
  updateIsMobileTimelinePanelSettledOpen,
  updateAiImageState,
  updateAiSubtitleState,
  updateAiVoiceState,
  addAiVideoGenerationItem,
  addAiImageEditGenerationItem,
  removeAiImageEditGenerationItem,
  addAiImageGenerationItem,
  removeAiImageGenerationItem,
  addAiImageGenerationFailure,
  removeAiImageGenerationFailure,
  markAiVideoGenerationFailed,
  removeAiVideoGenerationItem,
  setAiCredits,
  addPosterAnimationGenerationItem,
  markPosterAnimationGenerationFailed,
  removePosterAnimationGenerationItem,
  setHasUserSeenImageEdit,
  setHasUserSeenImageUpscale,
  updateIsUploadingToMyUploads,
  updatePublishDropDownState,
  updateShareDropDownState,
  setInputSuggestions,
  setBusinessAddress,
  updateCopiedStylesState,
  updateAutoSaveTooltipState,
  updateCurrentUserId,
  updateRecordOptionsOpenState,
  updateFeatureToShowOnLoad,
  updateFeatureDemo,
  updateIsPosterShared,
  updateIsPosterSharedInTeam,
  updateSaveConflictDataDetails,
  updateVersionHistoryModeState,
  updatePageIdToScrollToOnWhiteboardResize,
  updateFitToScreenOnWhiteboardResize,
  updateIsDesktopTimelineToggleOn,
  updatePosterScaling,
  setMobilePageSelectionModeState,
  updatePagePreview,
  removePagePreview,
  updateWebDownloadPanelOpenState,
  updateIsPosterInitialized,
  requestOpenAnimationPanel,
  clearOpenAnimationPanelRequest,
  requestCloseAnimationPanel,
  clearCloseAnimationPanelRequest,
  requestOpenBackgroundTransitionPanel,
  clearOpenBackgroundTransitionPanelRequest,
  setActiveTransitionEditorBoundaryId,
  setSelectedTransitionBoundaryId,
  setIsAnimationPanelActiveInSidebar,
  updateTriggerOpenTemplatesPopup,
  updateTriggerOpenMyDesignsPopup,
  updateIsOnMyDesignExpanded,
  updateLayersPanelState,
  updateTriggerOpenPosterAnimationPanel,
} = posterEditorSlice.actions;
export const posterEditorReducer = posterEditorSlice.reducer;

/** True while any USER-uploaded media is still uploading — a panel-level upload with no poster item yet (isUploadingToMyUploads), or a user-upload item resolving its temp data URL. Stock/AI temp items never count. */
export const getIsUploadingUserMedia = (state: RootState): boolean => {
  if (state.posterEditor.isUploadingToMyUploads) {
    return true;
  }
  return state.posterEditor.posterObject?.uploadingImages.some((entry) => isUserUploadedImageSource(entry.source)) ?? false;
};

/** The selected audio uid when EXACTLY one clip is selected — a multi-selection reads as "nothing selected" for single-clip surfaces. */
export const getSingleSelectedAudioItemUID = (state: RootState): string | undefined => {
  const selectedUIDs = state.posterEditor.selectedAudioItemUIDs;
  return selectedUIDs.length === 1 ? selectedUIDs[0] : undefined;
};

export const getSelectedAudioItem = createSelector(
  [
    (state: RootState): Record<string, AudioItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips.audioItemsHashMap;
    },
    getSingleSelectedAudioItemUID,
  ],
  (audioItemHashMap, selectedAudioItemUID) => {
    if (!audioItemHashMap || !selectedAudioItemUID) {
      return undefined;
    }

    return audioItemHashMap[selectedAudioItemUID];
  }
);

export const getAudioItemFromCurrentPageById = createSelector(
  [
    (state: RootState): Record<string, AudioItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips?.audioItemsHashMap;
    },
    (_state: RootState, audioItemId: string): string => audioItemId,
  ],
  (audioItemsHashMap, audioItemId) => {
    if (!audioItemsHashMap || !audioItemId) {
      return undefined;
    }
    return audioItemsHashMap[audioItemId];
  }
);

export const getSelectedAudioItemHasFade = createSelector([getSelectedAudioItem], (audioItem): boolean => {
  if (!audioItem) {
    return false;
  }
  return audioItem.audioPlayer.fade.fadeInDuration !== 0 || audioItem.audioPlayer.fade.fadeOutDuration !== 0;
});

export const getPosterObject = (state: RootState): PosterObject | null => {
  return state.posterEditor.posterObject;
};

export const getCopiedStyles = (state: RootState): CopyableItemStyles | undefined => {
  return state.posterEditor.copiedStyles;
};

export const selectItem = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap;
    },
    (_state: RootState, itemId: string): string => itemId,
  ],
  (pages, itemId) => {
    if (!pages) {
      return undefined;
    }

    for (const [, page] of Object.entries(pages)) {
      if (page.items.itemsHashMap[itemId]) {
        return page.items.itemsHashMap[itemId];
      }
    }

    return undefined;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const isPagePlaying = (state: RootState, pageId: string): boolean => {
  return state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.isPlaying ?? false;
};

export const getIsCurrentPagePlaying = (state: RootState): boolean => {
  const currentPageId = state.posterEditor.currentPageId;
  return state.posterEditor.posterObject?.pages?.pagesHashMap[currentPageId]?.isPlaying ?? false;
};

export const isPagePlaybackEnded = createSelector(
  [
    (state: RootState, pageId: string): number | undefined => {
      const page = state.posterEditor.posterObject?.pages?.pagesHashMap[pageId];
      return page ? getPageDuration(page) : undefined;
    },
    (state: RootState, pageId: string): number => getPagePlaybackTime(state, pageId),
    (_state: RootState, pageId: string): string => pageId,
  ],
  (pageDuration, currentTime) => {
    if (pageDuration === undefined) {
      return false;
    }

    return Math.abs(currentTime - pageDuration) < PAGE_END_TIME_TOLERANCE_SECONDS;
  }
);

export const getPagePlaybackIcon = createSelector(
  [
    (state: RootState, pageId: string): boolean => isPagePlaying(state, pageId),
    (_state: RootState, pageId: string): string => pageId,
  ],
  (playing) => {
    if (playing) {
      return 'icon-pause';
    }

    return 'icon-play-video';
  }
);

export const getCurrentPageDuration = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const id = state.posterEditor.currentPageId;
      return id ? state.posterEditor.posterObject?.pages.pagesHashMap[id]?.items : undefined;
    },
    (state: RootState): AudioClipsObject | undefined => {
      const id = state.posterEditor.currentPageId;
      return id ? state.posterEditor.posterObject?.pages.pagesHashMap[id]?.audioClips : undefined;
    },
  ],
  (items, audioClips): number | undefined => {
    if (!items) {
      return undefined;
    }
    return computePageDuration(items, audioClips);
  }
);

export const getCurrentPageTime = (state: RootState): number => {
  return getPagePlaybackTime(state, state.posterEditor.currentPageId);
};

export const getCurrentPageTimeFloored = (state: RootState): number => {
  return Math.floor(getCurrentPageTime(state));
};

export const getCurrentPageTimeRounded = (state: RootState): number => {
  return Math.round(getCurrentPageTime(state));
};

/** The current page's transitions collection from the mirror, if any. */
export const getCurrentPageTransitions = (state: RootState): PageTransitionsObject | undefined => {
  return state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId]?.transitions;
};

/** The boundary (see TransitionBoundaryId) whose transition is selected on the timeline, if any ('' in the mirror means none). */
export const getSelectedTransitionBoundaryId = (state: RootState): TransitionBoundaryId | undefined => {
  const selectedBoundaryId = getCurrentPageTransitions(state)?.selectedBoundaryId;
  if (!selectedBoundaryId) {
    return undefined;
  }
  return selectedBoundaryId;
};

/**
 * The single timeline item currently selected: the active canvas item (except transcripts), the selected audio clip,
 * or the selected transition boundary. Inputs are playback-stable references/primitives and the result is
 * shallow-compared, so the returned object identity survives playback ticks and unrelated store actions.
 */
// The single "selected timeline entity" read, derived entirely from the model: canvas
// objects (graphic items + backgrounds) from the fabric selection (activeItemIds), audio
// from the selectedAudioItemUIDs slice mirror, transition boundaries from the transitions
// mirror. Transcripts are skipped (their subtitles are the timeline-track selection, tracked
// on transcript.activeSubtitleId). Returns the {type, id} shape every timeline consumer reads.
export const getSelectedTimelineItem = createSelector(
  [
    (state: RootState): ItemObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      const activeItemIds = state.posterEditor.activeItemIds[currentPageId];
      if (activeItemIds?.length !== 1) {
        return undefined;
      }
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.items?.itemsHashMap?.[activeItemIds[0]];
    },
    getSingleSelectedAudioItemUID,
    getSelectedTransitionBoundaryId,
  ],
  (selectedItem, selectedAudioUID, selectedTransitionBoundaryId): SelectedTimelineItem | undefined => {
    if (selectedItem) {
      if (selectedItem.gitype === ITEM_TYPE.TRANSCRIPT) {
        return undefined;
      }
      return {type: selectedItem.isBackground ? TimelineItemType.BACKGROUND : TimelineItemType.ITEM, id: selectedItem.uid};
    }
    if (selectedAudioUID) {
      return {type: TimelineItemType.AUDIO, id: selectedAudioUID};
    }
    if (selectedTransitionBoundaryId) {
      return {type: TimelineItemType.TRANSITION, id: selectedTransitionBoundaryId};
    }
    return undefined;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getPageCurrentTime = (state: RootState, pageId: string): number => {
  return getPagePlaybackTime(state, pageId);
};

export const getPosterDuration = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap;
    },
  ],
  (pages) => {
    if (!pages) {
      return undefined;
    }

    let totalDuration = 0;
    for (const [, page] of Object.entries(pages)) {
      totalDuration += getPageDuration(page);
    }

    return totalDuration;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

const getFirstBackground = (items: PageItemsObject): ItemObject | undefined => {
  for (const uid of items.itemsOrder) {
    const item = items.itemsHashMap[uid];
    if (item?.isBackground) {
      return item;
    }
  }
  return undefined;
};

// Returns the bg whose [onPageStartTime, onPageEndTime) contains the page's current time.
// Mirrors PageItems.getActiveBackground — picks the latest-starting match so that during
// a transition overlap the incoming bg wins. A page with no time-based content has no
// timeline to be active on: its single background carries no span (the invariant
// MigrateToVersion71 establishes), so on a time-window miss the zero-duration page
// resolves to its first background instead of reporting none.
export const getCurrentPageBackground = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return currentPageId ? state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items : undefined;
    },
    (state: RootState): AudioClipsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return currentPageId ? state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.audioClips : undefined;
    },
    (state: RootState): number => getCurrentPageTime(state),
  ],
  (items, audioClips, currentTime): ItemObject | undefined => {
    if (!items) {
      return undefined;
    }
    for (let i = items.itemsOrder.length - 1; i >= 0; i--) {
      const item = items.itemsHashMap[items.itemsOrder[i]];
      if (item?.isBackground && currentTime >= item.onPageStartTime && currentTime < item.onPageEndTime) {
        return item;
      }
    }
    if (computePageDuration(items, audioClips) === 0) {
      return getFirstBackground(items);
    }
    return undefined;
  }
);

export const getCurrentPageBackgroundsInOrder = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return currentPageId ? state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items : undefined;
    },
  ],
  (items): ItemObject[] => {
    if (!items) {
      return [];
    }
    const bgs: ItemObject[] = [];
    for (const uid of items.itemsOrder) {
      const item = items.itemsHashMap[uid];
      if (item?.isBackground) {
        bgs.push(item);
      }
    }
    bgs.sort((a, b) => a.onPageStartTime - b.onPageStartTime);
    return bgs;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getPageBackgroundByUid = (state: RootState, pageId: string, backgroundUid: string): ItemObject | undefined => {
  const item = state.posterEditor.posterObject?.pages.pagesHashMap?.[pageId]?.items.itemsHashMap[backgroundUid];
  return item?.isBackground ? item : undefined;
};

// The background that is the current single active selection, or undefined when
// nothing (or something else) is selected. No playhead fallback — use this to tell
// whether the user is editing a background by selecting it.
export const getSelectedBackground = (state: RootState): ItemObject | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  const activeIds = state.posterEditor.activeItemIds[currentPageId];
  if (activeIds?.length === 1) {
    const selected = state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap[activeIds[0]];
    if (selected?.isBackground) {
      return selected;
    }
  }
  return undefined;
};

// The background the user is editing: the selected background when a single
// background is the active selection, otherwise the background under the
// playhead. Lets the background panels target a background by identity so an
// edit stays on it while the clock moves, instead of following the playhead.
export const getBackgroundBeingEdited = (state: RootState): ItemObject | undefined => {
  return getSelectedBackground(state) ?? getCurrentPageBackground(state);
};


export const getCurrentPageHashedID = (state: RootState): string | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.hashedID;
};


export const getCurrentPageItems = (state: RootState): PageItemsObject | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
};

export const getCurrentPageAppliedPalette = (state: RootState): ColorPaletteInfo | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.appliedPalette;
};

export const getNumberOfPages = (state: RootState): number => {
  if (!state.posterEditor.posterObject?.pages.pagesHashMap) {
    return 0;
  }
  return Object.keys(state.posterEditor.posterObject.pages.pagesHashMap).length;
};

export const getIsPosterInitialized = (state: RootState): boolean => {
  return state.posterEditor.isPosterInitialized;
};

export const getIsOnMyDesignExpanded = (state: RootState): boolean => {
  return state.posterEditor.isOnMyDesignExpanded;
};

export const getHasShownOnboardingPopup = (state: RootState): boolean => {
  return state.posterEditor.hasShownOnboardingPopup;
};

export const getHasShownItemAnimationOnboardingPopup = (state: RootState): boolean => {
  return state.posterEditor.hasShownItemAnimationOnboardingPopup;
};

export const isScaleAnimatingToFitToScreen = createSelector(
  [
    (state: RootState): boolean => {
      return state.posterEditor.posterObject?.scaling.isScaleAnimating === true;
    },
    (state: RootState): boolean => {
      return state.posterEditor.posterObject?.scaling.isCurrentScaleBestFit === true;
    },
  ],
  (isScaleAnimating, isCurrentScaleBestFit) => {
    return isScaleAnimating && isCurrentScaleBestFit;
  }
);

export const getNumberOfItemsInCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
  ],
  (itemsHashMap) => {
    if (!itemsHashMap) {
      return 0;
    }

    return Object.keys(itemsHashMap).length;
  }
);

export const getAllTranscriptItemObjects = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages.pagesHashMap;
    },
  ],
  (pages) => {
    if (!pages) {
      return {};
    }
    const transcriptItemObjectsHashmap: Record<string, TranscriptItemObject> = {};
    for (const page of Object.values(pages)) {
      for (const item of Object.values(page.items.itemsHashMap)) {
        if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
          transcriptItemObjectsHashmap[item.uid] = item as TranscriptItemObject;
        }
      }
    }

    return transcriptItemObjectsHashmap;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getTranscriptItemsInCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
  ],
  (itemsHashMap): TranscriptItemObject[] => {
    if (!itemsHashMap) {
      return [];
    }
    return Object.values(itemsHashMap).filter((item): item is TranscriptItemObject => item.gitype === ITEM_TYPE.TRANSCRIPT);
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getAllSelectedItemObjectsOnCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
    (state: RootState): string[] | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.activeItemIds[currentPageId];
    },
  ],
  (itemsHashMap, selectedIdsOnPage) => {
    if (!itemsHashMap || !selectedIdsOnPage || selectedIdsOnPage.length === 0) {
      return {};
    }

    const selectedItemObjects: Record<string, ItemObject> = {};
    for (const id of selectedIdsOnPage) {
      const item = itemsHashMap[id];
      if (item) {
        selectedItemObjects[id] = item;
      }
    }

    return selectedItemObjects;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

/**
 * returns item object if it is the only item selected otherwise returns null
 */
export const getSingleActiveItemObjectOnPage = (state: RootState, pageId?: string): ItemObject | null => {
  const activeItemIdsOnPage = state.posterEditor.activeItemIds[pageId ?? state.posterEditor.currentPageId];
  if (activeItemIdsOnPage?.length !== 1) {
    return null;
  }

  return state.posterEditor.posterObject?.pages.pagesHashMap[pageId ?? state.posterEditor.currentPageId].items.itemsHashMap[activeItemIdsOnPage[0]] ?? null;
};

export const getCurrentPageVideoItemsWithAudioInOrder = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
    },
  ],
  (items): VideoItemObject[] => {
    if (!items) {
      return [];
    }

    const videoItems: VideoItemObject[] = [];

    for (const itemId of items.itemsOrder) {
      const item = items.itemsHashMap[itemId];
      if (item.gitype === ITEM_TYPE.VIDEO) {
        videoItems.push(item as VideoItemObject);
      }
    }

    return videoItems;
  }
);

// returns audio items in order of their onPageStartTime (using name as a tie-breaker)
export const getAudioItemsInOrder = createSelector(
  [
    (state: RootState): AudioClipsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips;
    },
  ],
  (audioClipsObject): AudioItemObject[] => {
    const audioItems: AudioItemObject[] = [];
    if (audioClipsObject) {
      audioItems.push(...Object.values(audioClipsObject.audioItemsHashMap));
    }

    audioItems.sort((a, b) => {
      if (a.onPageStartTime !== b.onPageStartTime) {
        return a.onPageStartTime - b.onPageStartTime;
      }
      return a.name.localeCompare(b.name);
    });

    return audioItems;
  }
);


export const getTextItems = createSelector(
  [
    (state: RootState): PosterPageObject | undefined => {
      return state.posterEditor.posterObject?.pages;
    },
  ],
  (pages): (TextItemObject | FancyTextItemObject)[] => {
    if (!pages) return [];

    return Object.values(pages.pagesHashMap).flatMap((page) => {
      const items = Object.values(page.items.itemsHashMap);

      return items.filter((item): item is TextItemObject | FancyTextItemObject => {
        return item.gitype === ITEM_TYPE.TEXT || item.gitype === ITEM_TYPE.FANCY_TEXT;
      });
    });
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

const selectAiVideos = (state: RootState): AiVideosState[] => state.posterEditor.aiState.aiVideo;

export const selectGeneratingAiVideos = createSelector([selectAiVideos], (aiVideo): AiVideosState[] => aiVideo.filter((item) => item.status === AiVideoGenerationStatus.GENERATING), {
  memoizeOptions: {resultEqualityCheck: shallowEqual},
});

export const selectFailedAiVideos = createSelector([selectAiVideos], (aiVideo): AiVideosState[] => aiVideo.filter((item) => item.status === AiVideoGenerationStatus.ERROR), {
  memoizeOptions: {resultEqualityCheck: shallowEqual},
});

export const selectGeneratingAiVideoCount = createSelector([selectGeneratingAiVideos], (generating): number => generating.length);

export const selectIsAnyAiVideoGenerating = createSelector([selectGeneratingAiVideos], (generating): boolean => generating.length > 0);

export const selectHasFailedAiVideo = createSelector([selectFailedAiVideos], (failed): boolean => failed.length > 0);

const selectAiImageGenerationFailures = (state: RootState): AiImageGenerationFailureState[] => state.posterEditor.aiState.aiImageGenerationFailures;

export const selectFailedAiImageGenerations = createSelector(
  [selectAiImageGenerationFailures],
  (failures): AiImageGenerationFailureState[] => failures.filter((item) => item.status === AiImageGenerationFailureStatus.ERROR),
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const selectHasFailedAiImageGeneration = createSelector([selectFailedAiImageGenerations], (failed): boolean => failed.length > 0);

export const selectPendingAiImageGenerationRetry = (state: RootState): AiImageGenerationFailureState | null =>
  state.posterEditor.aiState.aiImageGenerationFailures.find((item) => item.status === AiImageGenerationFailureStatus.PENDING_RETRY) ?? null;

export const selectPendingAiVideoRetry = (state: RootState): AiVideosState | null =>
  state.posterEditor.aiState.aiVideo.find((item) => item.status === AiVideoGenerationStatus.PENDING) ?? null;

export const selectAiCredits = (state: RootState): number => state.posterEditor.aiState.aiCredits ?? 0;

export const selectAiCreditsOrNull = (state: RootState): number | null => state.posterEditor.aiState.aiCredits;

const selectPosterAnimations = (state: RootState): PosterAnimationGenerationState[] => state.posterEditor.aiState.aiPosterAnimation;

export const selectGeneratingPosterAnimations = createSelector(
  [selectPosterAnimations],
  (animations): PosterAnimationGenerationState[] => animations.filter((item) => item.status === PosterAnimationGenerationStatus.GENERATING),
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

/** Deliberately unfiltered: the concurrent-generation cap and the global toast count every source. */
export const selectGeneratingPosterAnimationCount = (state: RootState): number => selectGeneratingPosterAnimations(state).length;

export const selectFailedPosterAnimations = createSelector(
  [selectPosterAnimations],
  (animations): PosterAnimationGenerationState[] => animations.filter((item) => item.status === PosterAnimationGenerationStatus.ERROR),
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

/**
 * Page and item panels own separate grids and separate histories, so anything they render per-source —
 * skeletons, error cards, insert positions — has to be filtered too. Selectors are built once per
 * source so each keeps its own memoised result and useAppSelector stays referentially stable.
 */
const makePosterAnimationsForSourceSelector = (status: PosterAnimationGenerationStatus, sourceType: AiAnimationSourceType): ((state: RootState) => PosterAnimationGenerationState[]) =>
  createSelector(
    [selectPosterAnimations],
    (animations): PosterAnimationGenerationState[] => animations.filter((item) => item.status === status && item.params.sourceType === sourceType),
    {memoizeOptions: {resultEqualityCheck: shallowEqual}}
  );

const generatingPosterAnimationSelectors: Record<AiAnimationSourceType, (state: RootState) => PosterAnimationGenerationState[]> = {
  [AiAnimationSourceType.PAGE]: makePosterAnimationsForSourceSelector(PosterAnimationGenerationStatus.GENERATING, AiAnimationSourceType.PAGE),
  [AiAnimationSourceType.ITEM]: makePosterAnimationsForSourceSelector(PosterAnimationGenerationStatus.GENERATING, AiAnimationSourceType.ITEM),
};

const failedPosterAnimationSelectors: Record<AiAnimationSourceType, (state: RootState) => PosterAnimationGenerationState[]> = {
  [AiAnimationSourceType.PAGE]: makePosterAnimationsForSourceSelector(PosterAnimationGenerationStatus.ERROR, AiAnimationSourceType.PAGE),
  [AiAnimationSourceType.ITEM]: makePosterAnimationsForSourceSelector(PosterAnimationGenerationStatus.ERROR, AiAnimationSourceType.ITEM),
};

export const selectGeneratingPosterAnimationsForSource = (state: RootState, sourceType: AiAnimationSourceType): PosterAnimationGenerationState[] =>
  generatingPosterAnimationSelectors[sourceType](state);

export const selectGeneratingPosterAnimationCountForSource = (state: RootState, sourceType: AiAnimationSourceType): number =>
  selectGeneratingPosterAnimationsForSource(state, sourceType).length;

export const selectFailedPosterAnimationsForSource = (state: RootState, sourceType: AiAnimationSourceType): PosterAnimationGenerationState[] =>
  failedPosterAnimationSelectors[sourceType](state);

export const selectPendingPosterAnimationRetry = (state: RootState): PosterAnimationGenerationState | null =>
  state.posterEditor.aiState.aiPosterAnimation.find((item) => item.status === PosterAnimationGenerationStatus.PENDING) ?? null;

export const getActivityIndicatorTypeForAi = createSelector(
  [(state: RootState): AiState => state.posterEditor.aiState, selectIsAnyAiVideoGenerating, selectHasFailedAiVideo],
  (aiState, hasGeneratingVideo, hasErroredVideo): ActivityIndicatorType | undefined => {
    if (aiState.aiImage === AiImageState.GENERATING || aiState.aiSubtitles === AiSubtitleState.GENERATING || hasGeneratingVideo) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiState.aiSubtitles === AiSubtitleState.READY_FAILED || hasErroredVideo) {
      return ActivityIndicatorType.ERROR;
    }
    if (aiState.aiImage === AiImageState.READY || aiState.aiSubtitles === AiSubtitleState.READY_SUCCESS) {
      return ActivityIndicatorType.SUCCESS;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiImage = createSelector(
  [
    (state: RootState): AiImageState => {
      return state.posterEditor.aiState.aiImage;
    },
  ],
  (aiImageStatus): ActivityIndicatorType | undefined => {
    if (aiImageStatus === AiImageState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiImageStatus === AiImageState.READY) {
      return ActivityIndicatorType.SUCCESS;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiSubtitle = createSelector(
  [
    (state: RootState): AiSubtitleState => {
      return state.posterEditor.aiState.aiSubtitles;
    },
  ],
  (aiSubtitleStatus): ActivityIndicatorType | undefined => {
    if (aiSubtitleStatus === AiSubtitleState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiSubtitleStatus === AiSubtitleState.READY_SUCCESS) {
      return ActivityIndicatorType.SUCCESS;
    }
    if (aiSubtitleStatus === AiSubtitleState.READY_FAILED) {
      return ActivityIndicatorType.ERROR;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiVoice = createSelector(
  [
    (state: RootState): AiVoiceState => {
      return state.posterEditor.aiState.aiVoice;
    },
  ],
  (aiVoiceStatus): ActivityIndicatorType | undefined => {
    if (aiVoiceStatus === AiVoiceState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiVoiceStatus === AiVoiceState.READY) {
      return ActivityIndicatorType.SUCCESS;
    }
    if (aiVoiceStatus === AiVoiceState.ERROR) {
      return ActivityIndicatorType.ERROR;
    }
    return undefined;
  }
);

export const isPageVideo = (state: RootState, pageId: string): boolean => {
  const page = state.posterEditor.posterObject?.pages?.pagesHashMap[pageId];
  if (!page) {
    return false;
  }
  return isPageVideoUtil(page);
};

/**
 * Raw user-controlled flag: did the user open the timeline view (the desktop bottom bar or the
 * mobile timeline panel)? Prefer `selectIsTimelineVisible` unless you specifically need the toggle.
 */
export const selectIsDesktopTimelineToggleOn = (state: RootState): boolean => {
  return state.posterEditor.isDesktopTimelineToggleOn === true;
};

/**
 * Can the timeline be offered to the user at all: the current page is a video and no mode that
 * hides the timeline (version history, mobile page selection) is active. On mobile this is also
 * when the collapsed "edit timeline" pill shows.
 */
export const selectCanTimelineBeShown = (state: RootState): boolean => {
  if (state.posterEditor.isVersionHistoryModeActive) {
    return false;
  }
  if (state.posterEditor.mobilePageSelectionModeState === MobilePageSelectionModeState.ACTIVE) {
    return false;
  }
  const currentPageId = state.posterEditor.currentPageId;
  return !!currentPageId && isPageVideo(state, currentPageId);
};

/**
 * Is the timeline (bottom bar on desktop, bottom panel on mobile) visible to the user?
 * Both variants open collapsed: the page is a video and the user has toggled timeline mode on.
 */
export const selectIsTimelineVisible = (state: RootState): boolean => {
  return selectCanTimelineBeShown(state) && selectIsDesktopTimelineToggleOn(state);
};

const selectIsMobileTimelinePanelSettledOpen = (state: RootState): boolean => {
  return state.posterEditor.isMobileTimelinePanelSettledOpen === true;
};

/**
 * The phone's whiteboard keeps the pill row's height reserved until the timeline panel has fully opened,
 * and reclaims it only once the panel has fully closed — so the canvas refits once per toggle, after the
 * panel animation, never during it.
 */
export const selectIsMobileTimelinePillRowReserved = (state: RootState): boolean => {
  return state.posterEditor.isMobileVariant === true && selectCanTimelineBeShown(state) && !selectIsMobileTimelinePanelSettledOpen(state);
};

/** The phone's collapsed-timeline pill: shown while the timeline is closed and its panel has finished leaving. */
export const selectIsMobileTimelinePillVisible = (state: RootState): boolean => {
  return selectIsMobileTimelinePillRowReserved(state) && !selectIsDesktopTimelineToggleOn(state);
};

/**
 * Is the whiteboard laid out in single-page (zoomed) mode rather than scrolling through pages?
 * Only on mobile. Desktop keeps every page visible and freely scrollable, including in timeline mode.
 */
export const selectIsSinglePageLayout = (state: RootState): boolean => {
  return state.posterEditor.isMobileVariant === true;
};

export const selectIsCurrentPageModifying = (state: RootState): boolean => {
  const pageId = state.posterEditor.currentPageId;
  if (!pageId) {
    return false;
  }
  return state.posterEditor.arePosterItemsMoving[pageId] || state.posterEditor.arePosterItemsScaling[pageId] || state.posterEditor.arePosterItemsRotating[pageId];
};

export const selectLayerPanelRows = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
    },
  ],
  (items): LayerEntry[] => {
    if (!items) {
      return [];
    }
    const entries: LayerEntry[] = [];
    const memberUidsByGroupId = new Map<string, string[]>();
    const seenUids = new Set<string>();
    for (let i = items.itemsOrder.length - 1; i >= 0; i -= 1) {
      const uid = items.itemsOrder[i];
      const item = items.itemsHashMap[uid];
      if (!item || item.gitype === ITEM_TYPE.TRANSCRIPT || item.isBackground || seenUids.has(uid)) {
        continue;
      }
      seenUids.add(uid);
      if (item.groupId) {
        const existing = memberUidsByGroupId.get(item.groupId);
        if (existing) {
          existing.push(uid);
          continue;
        }
        const memberUids = [uid];
        memberUidsByGroupId.set(item.groupId, memberUids);
        entries.push({layerType: LayerType.Group, groupId: item.groupId, memberUids});
      } else {
        entries.push({layerType: LayerType.Item, uid});
      }
    }
    return entries;
  },
  {memoizeOptions: {resultEqualityCheck: areLayerEntriesEqual}}
);
